import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, UserPlus, LogOut, FileText, Package, Wrench, UsersRound, Dumbbell, Award, CheckSquare, KeyRound, Bell, ScanLine, ClipboardList, BarChart3, Sparkles, Settings, Building2, UserCheck, ShieldCheck, ShoppingCart, Boxes, Wallet } from "lucide-react";
import { applyTheme, loadLocal, saveLocal } from "../lib/theme";
import { rank, ROLE_LABELS } from "../lib/rbac";
import axios from "axios";

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  
  const navItems = [
    { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
    { name: "Branches", path: "/admin/branches", icon: Building2 },
    { name: "Members", path: "/admin/members", icon: Users },
    { name: "Attendance", path: "/admin/attendance", icon: ScanLine },
    { name: "Staff Attendance", path: "/admin/staff-attendance", icon: UserCheck },
    { name: "Leads", path: "/admin/leads", icon: UserPlus },
    { name: "Tasks", path: "/admin/tasks", icon: CheckSquare },
    { name: "Packages", path: "/admin/packages", icon: Award },
    { name: "Billing", path: "/admin/invoices", icon: FileText },
    { name: "Inventory", path: "/admin/inventory", icon: Package },
    { name: "PT & Classes", path: "/admin/trainers", icon: Dumbbell },
    { name: "Plans", path: "/admin/plans", icon: ClipboardList },
    { name: "Equipment", path: "/admin/equipment", icon: Wrench },
    { name: "Staff", path: "/admin/hr", icon: UsersRound },
    { name: "Security", path: "/admin/security", icon: ShieldCheck },
    { name: "Procurement", path: "/admin/procurement", icon: ShoppingCart },
    { name: "Stock Register", path: "/admin/stock-register", icon: Boxes },
    { name: "Petty Cash", path: "/admin/petty-cash", icon: Wallet },
    { name: "Reports", path: "/admin/reports", icon: BarChart3 },
    { name: "Housekeeping", path: "/admin/housekeeping", icon: Sparkles },
    { name: "Settings", path: "/admin/settings", icon: Settings },
  ];

  useEffect(() => {
    const local = loadLocal(); if (local) applyTheme(local);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/theme`, { credentials: "include" })
      .then(r => r.json()).then(d => { if (d && d.theme) { applyTheme(d.theme); saveLocal(d.theme); } }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`)
      .then(res => setUser(res.data.user))
      .catch(() => navigate("/login"));
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`);
    } catch(e) {
      console.error("Logout failed", e);
    }
    navigate("/login");
  };

  const filteredNavItems = navItems.filter(item => {
    if (!user) return false;
    const r = rank(user.role);
    const role = (user.role || "").toLowerCase();
    // Branches page: DM and above only
    if (item.name === "Branches") return r >= 70;
    // DM, GM, MD, CEO see everything
    if (r >= 70) return true;
    // Branch Manager: everything for their branch
    if (r >= 60) return item.name !== "Branches";
    if (role === "security") {
      return ["Dashboard", "Attendance", "Security", "Staff Attendance", "Housekeeping", "Settings"].includes(item.name);
    }
    if (role === "housekeeping") {
      return ["Dashboard", "Staff Attendance", "Housekeeping", "Settings"].includes(item.name);
    }
    if (role.includes("receptionist") || role.includes("front desk")) {
      return ["Dashboard", "Members", "Attendance", "Staff Attendance", "Leads", "Packages", "Billing", "PT & Classes", "Tasks", "Inventory", "Stock Register", "Petty Cash", "Reports", "Housekeeping", "Security", "Settings"].includes(item.name);
    }
    if (role.includes("trainer") || role.includes("coach") || role.includes("nutritionist")) {
      return ["Dashboard", "Attendance", "PT & Classes", "Plans", "Members", "Tasks", "Settings"].includes(item.name);
    }
    return ["Dashboard", "Settings"].includes(item.name);
  });

  if (!user) return <div className="h-screen flex items-center justify-center bg-main_bg"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-sidebar_bg"></div></div>;

  return (
    <div className="flex h-screen bg-main_bg text-text_primary font-sans admin-scope">
      
      {/* Sidebar */}
      <aside className="w-64 bg-sidebar_bg flex flex-col hidden lg:flex shrink-0 z-20 shadow-2xl">
        <div className="p-6 border-b border-sidebar_hover bg-sidebar_bg flex items-center gap-3">
          <img src="/logo.png" alt="SVT Golden" className="h-9 w-9 object-contain" onError={(e)=>{e.target.style.display='none'}} />
          <div>
            <h2 className="text-xl text-gold font-sans font-bold tracking-tight">SVT Golden</h2>
            <p className="text-[9px] uppercase tracking-[0.1em] text-sidebar_text mt-0.5">FITNESS MANAGEMENT</p>
          </div>
        </div>
        
        <nav className="flex-1 py-4 overflow-y-auto custom-scrollbar">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-6 py-3 transition-colors duration-200 border-l-4 ${
                  isActive 
                    ? "bg-sidebar_hover text-gold border-gold font-medium" 
                    : "text-sidebar_text border-transparent hover:text-text_primary hover:bg-sidebar_hover/50"
                }`}
              >
                <Icon size={18} strokeWidth={isActive ? 2.5 : 1.5} className={isActive ? "text-gold" : "text-sidebar_text"} />
                <span className="text-sm">{item.name}</span>
              </Link>
            );
          })}
        </nav>
        
        <div className="p-6 bg-sidebar_bg border-t border-sidebar_hover mt-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-full bg-gold text-sidebar_bg font-bold flex items-center justify-center shadow-md">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-text_primary text-sm font-medium truncate">{user.name}</p>
              <p className="text-sidebar_text text-[10px] truncate">{user.email}</p>
            </div>
          </div>
          <div className="space-y-3">
            <Link to="/admin/settings" className="flex items-center gap-2 text-xs text-sidebar_text hover:text-text_primary transition-colors">
              <Settings size={14} /> Settings & Theme
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-2 text-xs text-sidebar_text hover:text-accent_danger transition-colors w-full text-left">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-main_bg relative">
        
        {/* Top Header bar with dark/green tone */}
        <header className="h-16 bg-sidebar_bg border-b border-sidebar_hover flex items-center justify-between px-6 lg:px-8 shrink-0 sticky top-0 z-30 shadow-md">
          <div className="lg:hidden flex items-center gap-3">
            <h2 className="text-lg text-gold font-bold">SVT Golden</h2>
          </div>
          <div className="hidden lg:block"></div> {/* Spacer */}
          
          <div className="flex items-center gap-4">
            {user.branch && <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-sidebar_hover rounded-full text-xs font-semibold text-gold"><Building2 size={13} /> {user.branch}</span>}
            {rank(user.role) >= 80 && <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-sidebar_hover rounded-full text-xs font-semibold text-gold"><Building2 size={13} /> All Branches</span>}
            <button className="text-sidebar_text hover:text-text_primary transition-colors">
              <Bell size={20} />
            </button>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-sidebar_hover border border-sidebar_hover rounded-full cursor-pointer hover:bg-sidebar_hover/80 transition-colors">
              <div className="w-6 h-6 rounded-full bg-gold text-sidebar_bg flex items-center justify-center text-xs font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-text_primary hidden sm:block">{ROLE_LABELS[user.role] || user.role}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-8 pb-24 md:pb-12 relative z-10 scroll-smooth">
          <Outlet />
        </div>

        {/* Mobile Nav */}
        <nav className="lg:hidden fixed bottom-0 w-full flex justify-around items-center p-2 bg-sidebar_bg border-t border-sidebar_hover z-50 pb-safe overflow-x-auto gap-1 shadow-[0_-5px_20px_rgba(0,0,0,0.15)]">
          {filteredNavItems.slice(0,5).map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} className={`flex flex-col items-center p-2 rounded-xl transition-all min-w-[64px] ${isActive ? 'text-gold bg-sidebar_hover' : 'text-sidebar_text hover:text-text_primary'}`}>
                <Icon size={20} strokeWidth={isActive ? 2.5 : 1.5} className={isActive ? 'text-gold' : 'text-sidebar_text'} />
                <span className="text-[10px] mt-1.5 font-medium text-center truncate w-full">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </main>
    </div>
  );
}
